import { useState, useRef, useCallback } from 'react'
import { Upload, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const ACCEPT = 'audio/*,video/*'

const WAVEFORM_BARS = [
  { maxH: 20, dur: 0.8 },
  { maxH: 32, dur: 0.95 },
  { maxH: 24, dur: 1.1 },
  { maxH: 36, dur: 1.25 },
  { maxH: 16, dur: 1.4 },
]

interface AudioUploadHeroProps {
  onUpload: (files: File[]) => void
  onPasteUrl?: () => void
}

export function AudioUploadHero({ onUpload, onPasteUrl }: AudioUploadHeroProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const dragCounter = useRef(0)

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) setIsDragOver(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files).filter(
      f => f.type.startsWith('audio/') || f.type.startsWith('video/')
    )
    if (files.length > 0) onUpload(files)
  }, [onUpload])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) onUpload(files)
    e.target.value = ''
  }, [onUpload])

  return (
    <div
      className={cn(
        'relative w-full rounded-2xl border-2 border-dashed transition-all duration-200 animate-in fade-in zoom-in-95',
        isDragOver
          ? 'border-sky-400 bg-sky-50/30'
          : 'border-border bg-gradient-to-b from-secondary to-background'
      )}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="flex flex-col items-center justify-center py-16 px-6">
        <style>{`
          @keyframes waveform-0 { 0%,100%{height:8px} 50%{height:20px} }
          @keyframes waveform-1 { 0%,100%{height:8px} 50%{height:32px} }
          @keyframes waveform-2 { 0%,100%{height:8px} 50%{height:24px} }
          @keyframes waveform-3 { 0%,100%{height:8px} 50%{height:36px} }
          @keyframes waveform-4 { 0%,100%{height:8px} 50%{height:16px} }
        `}</style>
        <div className="flex items-center justify-center gap-1 mb-6" style={{ height: '36px' }}>
          {WAVEFORM_BARS.map((bar, i) => (
            <div
              key={i}
              className="w-1 rounded-full bg-sky-400"
              style={{
                height: '8px',
                animation: `waveform-${i} ${bar.dur}s ease-in-out infinite`,
              }}
            />
          ))}
        </div>

        <h3 className="text-lg font-semibold text-foreground mb-1">
          Drop your podcast audio
        </h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-xs">
          MP3, WAV, M4A, or video with audio — or wait for the AI to generate audio from text and it will show up here.
        </p>

        <div className="flex items-center gap-3">
          <Button
            onClick={() => inputRef.current?.click()}
            className="gap-2"
          >
            <Upload size={16} />
            Browse files
          </Button>
          {onPasteUrl && (
            <button
              onClick={onPasteUrl}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-secondary-foreground transition-colors"
            >
              <Link2 size={14} />
              Paste a URL
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        multiple
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  )
}
